import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '../components/ui/Button.js';
import { ErrorState } from '../components/ui/ErrorState.js';

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Last-resort boundary around the whole app. Feature areas add narrower boundaries later.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    override state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    override componentDidCatch(error: Error, info: ErrorInfo): void {
        console.error('Unhandled render error', error, info.componentStack);
    }

    override render(): ReactNode {
        if (this.state.hasError) {
            return (
                <main id="content" tabIndex={-1} style={{ padding: 'var(--space-6)' }}>
                    <ErrorState
                        title="Something went wrong"
                        description="The app hit an unexpected error. Reloading usually fixes it; your saved records are not affected."
                        action={
                            <Button
                                onClick={() => {
                                    window.location.reload();
                                }}
                            >
                                Reload
                            </Button>
                        }
                    />
                </main>
            );
        }
        return this.props.children;
    }
}
